<template>
  <div class="ask-main">
    <div
      ref="scrollContainer"
      class="ask-main-scroller"
    >
      <div
        ref="contentWrapper"
        class="ask-main-content"
      >
        <AskIntro />
        <div
          ref="topAnchor"
          style="height: 1px;"
        />
        <AskMessages @load-more="handleLoadMore" />
      </div>

      <div
        ref="bottomAnchor"
        style="height: 1px;"
      />
    </div>

    <div class="ask-main-form">
      <div class="ask-controls">
        <Badge
          class="ask-loader"
          :class="{'is-active': chatStore.toolActivityCount > 0}"
        >
          <Loader />
          {{ chatStore.lastToolDescription || 'Working' }}…
        </Badge>

        <div
          class="ask-scroll-to-bottom"
          :class="{ 'is-active': !shouldAutoScroll }"
        >
          <Badge
            icon-before="fas fa-arrow-down"
            size="3"
            clickable
            status="ask-anything"
            @click="scrollToAnchor(true)"
          >
            New messages
          </Badge>
        </div>
      </div>

      <AskChatForm />

      <p class="ask-disclaimer">
        The AI Assistant may make mistakes. We recommend checking important information.
      </p>
    </div>
  </div>
</template>

<script setup>
import { getCurrentInstance, ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { useChatStore } from '@frontend/ui/stores/chatStore';
import { Badge, Loader } from '@stakeui';

import AskIntro from './AskIntro.vue';
import AskChatForm from './AskChatForm.vue';
import AskMessages from './AskMessages.vue';

const chatStore = useChatStore();

const { proxy } = getCurrentInstance();

const scrollContainer = ref(null);
const topAnchor = ref(null);
const bottomAnchor = ref(null);
const contentWrapper = ref(null);
const shouldAutoScroll = ref(true); // true while user is at/near bottom

const scrollToAnchor = (top = false) => {
  const anchor = top ? topAnchor.value : bottomAnchor.value;
  if (!anchor) return;
  anchor.scrollIntoView({ behavior: 'auto', block: 'end' });
};

let intersectionObserver;
let resizeObserver;

const handleLoadMore = async (conversation, offsetTop, offsetHeight) => {
  const container = scrollContainer.value;
  if (!container) return;

  const distanceFromBottom = container.scrollHeight - (offsetTop + offsetHeight);

  await chatStore.fetchConversationMessages(
      conversation.id,
      conversation.messages.next_offset,
  );

  await nextTick(); // wait for DOM to render new messages

  const GAP_SIZE = 16;
  const scrollTarget = container.scrollHeight - container.clientHeight - distanceFromBottom + GAP_SIZE;

  scrollContainer.value.scrollTo({
    top: scrollTarget,
    behavior: 'smooth',
  });
};

async function handleConversationChange(id) {
  await chatStore.createSocket('/ws/agent-chat/', id || null);
  chatStore.resetToolActivity();
}

onMounted(async () => {
  const id = proxy.$route.params.conversationId;
  await handleConversationChange(id);

  await nextTick();
  scrollToAnchor(false);

  intersectionObserver = new IntersectionObserver(
      entries => {
        shouldAutoScroll.value = entries.some(e => e.isIntersecting);
      },
      { root: scrollContainer.value, threshold: 1.0 },
  );
  if (bottomAnchor.value) intersectionObserver.observe(bottomAnchor.value);

  // still keep ResizeObserver for content growth
  resizeObserver = new ResizeObserver(() => {
    if (shouldAutoScroll.value) {
      requestAnimationFrame(() => scrollToAnchor(false));
    }
  });
  resizeObserver.observe(contentWrapper.value);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  intersectionObserver?.disconnect();
});

watch(
    () => proxy.$route.params.conversationId,
    async (newId, oldId) => {
      if (newId === oldId) return;
      await handleConversationChange(newId);
    },
);
</script>

<style scoped>
.ask-main {
  --offset: 1.5rem;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

.ask-main-scroller {
  overflow-x: hidden;
  overflow-y: scroll;
  flex-grow: 1;
  position: relative;
}

.ask-main-content {
  padding: var(--offset);
  padding-bottom: calc(var(--offset) + 0.5rem);
}

.ask-main-form {
  flex-shrink: 0;
  padding: 0 var(--offset);
  position: relative;
}

.ask-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem var(--offset);
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  /* background: hsl(0deg 0% 100% / 80%); */
  /* backdrop-filter: blur(1px); */
  /* box-shadow: 0 0px 8px 8px white; */
}

.ask-loader,
.ask-scroll-to-bottom {
  opacity: 0;
  transition-duration: var(--stk-transition-duration);
  transition-property: opacity, transform;

  &.is-active {
    opacity: 1;
  }
}

.ask-loader {
  background: hsl(var(--stk-text-h) var(--stk-text-s) 94% / 80%);
  backdrop-filter: blur(1px);
}

.ask-disclaimer {
  font-size: 0.75em;
  padding: 1em;
  text-align: center;
  color: var(--stk-text-weak);
  text-align: center;
}
</style>
